import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  Paper,
  Grid,
  TextField,
  Typography,
  Button,
  Chip
} from '@material-ui/core';
import { withStyles } from '@material-ui/core';
import SharingService from '../services/DataSharingService';
import Loading from '../components/loading/Loading';
import history from '../../../history/browserHistory';
import SnackbarService from '../../../modules/core/services/SnackbarService';
import DataSharingHelper from '../helpers/DataSharingHelper';
import DateHelper from '../helpers/DateHelper';
import AreYouSureModal from './AreYouSureModal';
import AssetDataFeesInternational from "./AssetDataFeesInternational";
import AssetDataFees from "./AssetDataFees";

const snackbarService = SnackbarService.Instance;

const styles = (theme: any) => ({
  paper: {
    padding: theme.spacing.unit * 3,
    marginBottom: theme.spacing.unit * 3,
  },
  textFields: {
    marginTop: -theme.spacing.unit * 1.5,
    marginBottom: -theme.spacing.unit * 1.5,
    marginLeft: -theme.spacing.unit * 1.5,
  },
  textField: {
    margin: theme.spacing.unit * 1.5,
  },
  chips: {
    marginLeft: -theme.spacing.unit,
    marginBottom: -theme.spacing.unit,
  },
  chip: {
    margin: theme.spacing.unit,
  },
  roles: {
    marginTop: theme.spacing.unit * 3,
  },
  button: {
    marginRight: theme.spacing.unit * 2,
  },
  title: {
    marginBottom: theme.spacing.unit * 1.5,
  },
  feeHeading: {
    fontSize: 13,
  },
});

function groupFees(fees: any) {
  return fees.reduce((acc: any, curr: any) => {
    if (!acc[curr.id]) {
      acc[curr.id] = { id: curr.id, description: curr.description };
    }
    acc[curr.id][curr.data ? 'data' : 'transaction'] = curr.amount;
    return acc;
  }, {});
}

function AssetDataDetailsHOC(Label: any, DataProperty: any, Configuration: any) {
  class AssetDataDetails extends React.Component<any, any> {
    public state: any = {
      remark: '',
      agreement: null,
      fees: {},
      durationMonths: null,
      sending: false,
      areYouSure: '',
      onConfirmAreYouSure: null,
    };

    public async componentDidMount() {
      const agreement = await SharingService.GetAgreementDetails(this.props.match.params.agreementId);

      this.setState({
        agreement,
        fees: groupFees(this.filterFees(agreement.fees)),
        durationMonths: agreement.durationMonths
      });
    }
    
    public onChangeRemark = (event: any) => {
      this.setState({ remark: event.target.value });
    };

    public onChangeDurationMonths = (event: any) => {
      this.setState({ durationMonths: event.target.value });
    };

    public onChangeCurrency = (event: any) => {
      this.setState({
        ...this.state,
        agreement: {
          ...this.state.agreement,
          currency: event.target.value
        }
      })
    };
    
    public filterFees(fees: any) {
      // if international, only show data fees
      return this.props.asset.isInternational
          ? fees.filter((fee: any) => fee.data === true)
          : fees;
    }

    public onFeeUpdate = (event: any) => {
      this.setState({
        fees: {
          ...this.state.fees,
          [event.target.id]: {
            ...this.state.fees[event.target.id],
            data: event.target.value,
          },
        },
      });
    };

    public onCloseAreYouSure = () => {
      this.setState({ onConfirmAreYouSure: null });
    };

    public onClickAction = async (actionId: string) => {
      const {
        agreement: {
          states: [state],
        },
        onConfirmAreYouSure,
      } = this.state;

      const action = Configuration[state.id].actions[actionId];
      if (action.areYouSure && !onConfirmAreYouSure) {
        return this.setState({
          areYouSure: action.areYouSure,
          onConfirmAreYouSure: () => {
            this.onClickAction(actionId);
            this.onCloseAreYouSure();
          },
        });
      }

      try {
        this.setState({ sending: true });
        await action.onAction(this.state); // PostAgreementStateChange, e.g. change agreement state to Offered
        snackbarService.showSnackbar('success', action.successMessage);
        history.push(this.props.match.path.replace('/:agreementId', ''));
      } catch {
        this.setState({ sending: false });
      }
    };
    
    public renderEntity = () => {
      const {
        agreement: {
          [DataProperty.Entity]: { entity },
          subscription,
        },
      } = this.state;
      const { classes } = this.props;

      return (
        <React.Fragment>
          <Typography variant="title" className={classes.title}>
            {DataSharingHelper.CapitalizeString(Label.Entity.Name)}
          </Typography>
          <Paper className={classes.paper}>
            <div className={classes.textFields}>
              <Grid container>
                <Grid item>
                  <TextField
                    name="name"
                    label="Name"
                    value={entity.name}
                    disabled
                    className={classes.textField}
                  />
                  <TextField
                    name="nxtEntityId"
                    label="NxtEntityId"
                    value={entity.nxtPortId}
                    disabled
                    className={classes.textField}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    name="eori"
                    label="EORI"
                    value={entity.eori}
                    disabled
                    className={classes.textField}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    name="duns"
                    label="DUNS"
                    value={entity.duns}
                    disabled
                    className={classes.textField}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    name="vat"
                    label="VAT"
                    value={entity.vat}
                    disabled
                    className={classes.textField}
                  />
                </Grid>
              </Grid>
            </div>
            {DataProperty.Entity === 'subscription' && subscription.roles.length ? (
              <div className={classes.roles}>
                <Typography variant="title" gutterBottom={true}>
                  Roles
                </Typography>
                <div className={classes.chips}>
                  {subscription.roles.map((role: any) => (
                    <Chip key={role.id} label={role.name} className={classes.chip} />
                  ))}
                </div>
              </div>
            ) : null}
          </Paper>
        </React.Fragment>
      );
    };
    
    public renderFees = () => {
      const { agreement, fees } = this.state;
      const { classes, asset } = this.props;

      if (!Object.keys(fees).length) {
        return null;
      }

      return asset.isInternational
          ? <AssetDataFeesInternational
              agreement={agreement}
              fees={fees}
              classes={classes}
              configuration={Configuration}
              currency={this.state.agreement.currency}
              onFeeUpdate={this.onFeeUpdate}
              onChangeDurationMonths={this.onChangeDurationMonths}
              onChangeCurrency={this.onChangeCurrency}
          />
          : 
          <AssetDataFees
              agreement={agreement} 
              fees={fees}
              classes={classes}
              configuration={Configuration}
              onFeeUpdate={this.onFeeUpdate}
              onChangeDurationMonths={this.onChangeDurationMonths}
          />; 
    };

    public renderRemark = () => {
      const {
        agreement: {
          states: [state],
        },
        remark,
      } = this.state;
      const { classes } = this.props;

      if (!Configuration[state.id].sendRemark) {
        return null;
      }

      return (
        <React.Fragment>
          <Typography variant="title" className={classes.title}>
            Remarks shared with {Label.Entity.Name} (optional)
          </Typography>
          <Paper className={classes.paper}>
            <TextField
              id="remark"
              label="Remarks"
              placeholder="Write your remarks here"
              helperText={`Tip: Include your email and/or phone number so that the ${
                Label.Entity.Name
              } can contact you directly if needed.`}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 1000,
              }}
              multiline
              fullWidth
              value={remark}
              onChange={this.onChangeRemark}
            />
          </Paper>
        </React.Fragment>
      );
    };
    
    public renderAgreementDetails() {
      const {
        agreement: {
          states: [state],
        },
      } = this.state;
      const { classes } = this.props;

      return <Paper className={classes.paper}>
        <div className={classes.textFields}>
          <Grid container>
            <Grid item>
              <TextField
                  name="date"
                  label="Date"
                  value={DateHelper.FormatDate(state.date)}
                  disabled
                  className={classes.textField}
              />
            </Grid>
            <Grid item>
              <TextField
                  name="status"
                  label="Status"
                  value={Label.Status[state.id]}
                  disabled
                  className={classes.textField}
              />
            </Grid>
          </Grid>
        </div>
      </Paper>
    }
    
    public render() {
      if (!this.state.agreement) {
        return <Loading />;
      }
      
      const {
        agreement: {
          states: [state],
        },
        sending,
      } = this.state;
      const { match, classes } = this.props;

      return (
        <div>
          {this.renderAgreementDetails()}
          {this.renderEntity()}
          {this.renderFees()}
          {this.renderRemark()}
          <Link to={match.path.replace('/:agreementId', '')}>
            <Button variant="outlined" className={classes.button}>
              Back
            </Button>
          </Link>
          {Object.keys(Configuration[state.id].actions).map((actionId: any) => {
            const action = Configuration[state.id].actions[actionId];
            return (
                <Button
                    key={actionId}
                    variant="contained"
                    color="primary"
                    className={classes.button}
                    onClick={() => this.onClickAction(actionId)}
                    disabled={sending}
                >
                  {action.label}
                </Button>
            );
          })}
          <AreYouSureModal
              open={!!this.state.onConfirmAreYouSure}
              message={this.state.areYouSure}
              onClose={this.onCloseAreYouSure}
              onConfirm={this.state.onConfirmAreYouSure}
          />
        </div>
      );
    }
  }

  // @ts-ignore
  return withStyles(styles)((AssetDataDetails));
}

export default AssetDataDetailsHOC;



// WEBPACK FOOTER //
// ./src/modules/shared/dataSharing/AssetDataDetails.tsx